import {
    Terminal,
    FileEdit,
    Globe,
    Zap,
    Code2,
    Server,
    CheckCircle2,
    ArrowRight, Puzzle
} from 'lucide-react';

export default function Home() {
	return (
		<div className="w-full">
		{/* Hero Section */}
		<section className="relative overflow-hidden bg-vscode-editor border-b border-vscode-border">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
				<div className="text-center space-y-8">
					<div className="flex justify-center mb-8">
						<img src="/logo-dark.svg" alt="SakaDev" className="w-24 h-24" />
					</div>
					<h1 className="text-5xl md:text-7xl font-semibold font-sans">
						<span className="text-vscode-text-bright">SakaDev</span>
						<br />
						<span className="text-vscode-text">Advanced AI Development Assistant</span>
					</h1>
					<p className="text-xl md:text-2xl text-vscode-text-muted max-w-3xl mx-auto font-mono">
						AI-powered coding assistant that operates directly in VSCode, providing autonomous file creation/editing, command execution, browser automation, and intelligent code analysis - all with step-by-step user approval for safety and control.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
						<a
							href="https://marketplace.visualstudio.com/items?itemName=rahmanazhar.saka-dev"
							target="_blank"
							rel="noopener noreferrer"
							className="px-8 py-4 bg-vscode-accent hover:bg-vscode-accent-hover text-white rounded transition-colors font-medium text-lg flex items-center space-x-2 font-sans"
						>
							<span>Install for VS Code</span>
							<ArrowRight className="w-5 h-5" />
						</a>
						<a
							href="https://github.com/rahmanazhar/SakaDev"
							target="_blank"
							rel="noopener noreferrer"
							className="px-8 py-4 bg-vscode-sidebar text-vscode-text-bright border border-vscode-border hover:border-vscode-accent rounded transition-colors font-medium text-lg font-sans"
						>
							View on GitHub
						</a>
					</div>
				</div>
			</div>
		</section>

		{/* Key Features Grid */}
		<section className="py-20 bg-vscode-bg">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-16">
					<h2 className="text-4xl md:text-5xl font-semibold text-vscode-text-bright mb-4 font-sans">
						Powerful Features
					</h2>
					<p className="text-xl text-vscode-text-muted font-mono">
						Everything you need to code faster and smarter
					</p>
				</div>					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
						<FeatureCard
							icon={<Code2 className="w-8 h-8" />}
							title="🤖 Use Any AI Model"
							description="OpenRouter, Claude 4, GPT-4, Gemini 2.5 Flash, DeepSeek R1/V3, Mistral AI, xAI Grok, or local models via LM Studio/Ollama. Real-time cost tracking and token monitoring."
						/>
						<FeatureCard
							icon={<Terminal className="w-8 h-8" />}
							title="💻 Terminal Command Control"
							description="Direct terminal integration with real-time output monitoring, background process support, environment-aware execution, and granular auto-approval configuration."
						/>
						<FeatureCard
							icon={<FileEdit className="w-8 h-8" />}
							title="📄 Smart File Operations"
							description="Intelligent file creation and editing with real-time linter/compiler monitoring, automatic workspace checkpointing, visual diff comparisons, and change tracking."
						/>
						<FeatureCard
							icon={<Globe className="w-8 h-8" />}
							title="🌐 Browser Testing & Debugging"
							description="Headless browser automation, element interaction, screenshot capture, console monitoring, runtime debugging, and end-to-end testing workflows."
						/>
						<FeatureCard
							icon={<Puzzle className="w-8 h-8" />}
							title="🔧 Extensible Tool System"
							description="Create custom capabilities via MCP with on-demand tool creation, HTTP/SSE transport support, fine-grained permissions, and community marketplace."
						/>
						<FeatureCard
							icon={<CheckCircle2 className="w-8 h-8" />}
							title="🔍 Context-Aware Development"
							description="URL content import (@url), workspace diagnostics (@problems), direct file access (@file, @folder), Plan/Act mode workflow, and smart context management."
						/>
					</div>
				</div>
			</section>

			{/* API Providers Section */}
			<section className="py-20 bg-vscode-sidebar">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-16">
						<h2 className="text-4xl md:text-5xl font-semibold text-vscode-text-bright mb-4 font-sans">
							Use Any Model
						</h2>
						<p className="text-xl text-vscode-text-muted font-mono">
							Connect to leading AI providers or run models locally
						</p>
					</div>

					<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
						<ProviderBadge name="Anthropic" />
						<ProviderBadge name="OpenAI" />
						<ProviderBadge name="Google Gemini" />
						<ProviderBadge name="AWS Bedrock" />
						<ProviderBadge name="Azure" />
						<ProviderBadge name="OpenRouter" />
						<ProviderBadge name="Groq" />
						<ProviderBadge name="Cerebras" />
						<ProviderBadge name="Ollama" />
						<ProviderBadge name="LM Studio" />
						<ProviderBadge name="Mistral AI" />
						<ProviderBadge name="DeepSeek" />
					</div>
				</div>
			</section>

			{/* How It Works */}
			<section className="py-20 bg-vscode-editor border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-16">
						<h2 className="text-4xl md:text-5xl font-semibold text-vscode-text-bright mb-4 font-sans">
							How It Works
						</h2>
						<p className="text-xl text-vscode-text-muted font-mono">
							Four simple steps to autonomous coding
						</p>
					</div>

					<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
						<StepCard
							number="1"
							title="Task Initiation"
							description="Submit your task with optional images or screenshots to guide the process."
						/>
						<StepCard
							number="2"
							title="Project Analysis"
							description="SakaDev analyzes your codebase through file structure examination, AST parsing, regex searches, and efficient context management."
						/>
						<StepCard
							number="3"
							title="Intelligent Execution"
							description="Creates/edits files with automatic error detection, executes terminal commands with real-time monitoring, performs browser testing."
						/>
						<StepCard
							number="4"
							title="Task Completion"
							description="Provides summary of changes, verification steps, and one-click command execution to view results."
						/>
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-20 bg-vscode-sidebar border-t border-vscode-border">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
					<h2 className="text-4xl md:text-5xl font-semibold text-vscode-text-bright font-sans">
						Ready to Code Faster?
					</h2>
					<p className="text-xl text-vscode-text-muted font-mono">
						Join thousands of developers using SakaDev to ship better code, faster.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
						<a
							href="https://marketplace.visualstudio.com/items?itemName=rahmanazhar.saka-dev"
							target="_blank"
							rel="noopener noreferrer"
							className="px-8 py-4 bg-vscode-accent hover:bg-vscode-accent-hover text-white rounded transition-colors font-medium text-lg flex items-center space-x-2 font-sans"
						>
							<span>Get Started Free</span>
							<ArrowRight className="w-5 h-5" />
						</a>
						<a
							href="https://discord.gg/sakadev"
							target="_blank"
							rel="noopener noreferrer"
							className="px-8 py-4 bg-vscode-editor text-vscode-text-bright border border-vscode-border hover:border-vscode-accent rounded transition-colors font-medium text-lg font-sans"
						>
							Join Discord
						</a>
					</div>
				</div>
			</section>

			{/* Features Highlights */}
			<section className="py-20 bg-vscode-bg border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid md:grid-cols-3 gap-8">
						<HighlightCard
							icon={<CheckCircle2 className="w-6 h-6" />}
							title="Human-in-the-Loop"
							description="Step-by-step user approval for safety and control. Review diffs and explanations for full transparency."
						/>
						<HighlightCard
							icon={<Zap className="w-6 h-6" />}
							title="Context-Aware"
							description="Efficient project understanding through AST parsing, regex searches, and smart context management."
						/>
						<HighlightCard
							icon={<Server className="w-6 h-6" />}
							title="Extensible"
							description="Create custom capabilities via MCP. On-demand tool creation with fine-grained permissions."
						/>
					</div>
				</div>
			</section>
		</div>
	)
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
	return (
		<div className="p-6 bg-vscode-sidebar rounded border border-vscode-border hover:border-vscode-accent transition-colors">
			<div className="text-vscode-accent mb-4">{icon}</div>
			<h3 className="text-xl font-semibold text-vscode-text-bright mb-2 font-sans">{title}</h3>
			<p className="text-vscode-text font-mono text-sm">{description}</p>
		</div>
	)
}

function ProviderBadge({ name }: { name: string }) {
	return (
		<div className="flex items-center justify-center p-4 bg-vscode-sidebar rounded border border-vscode-border hover:border-vscode-accent transition-colors">
			<span className="text-sm font-medium text-vscode-text font-mono">{name}</span>
		</div>
	)
}

function StepCard({ number, title, description }: { number: string; title: string; description: string }) {
	return (
		<div className="text-center space-y-4">
			<div className="w-16 h-16 mx-auto rounded-full bg-vscode-accent text-white flex items-center justify-center text-2xl font-bold border-2 border-vscode-accent">
				{number}
			</div>
			<h3 className="text-xl font-semibold text-vscode-text-bright font-sans">{title}</h3>
			<p className="text-vscode-text font-mono text-sm">{description}</p>
		</div>
	)
}

function HighlightCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
	return (
		<div className="flex flex-col items-center text-center space-y-4 p-6">
			<div className="p-3 rounded bg-vscode-accent/20 text-vscode-accent">{icon}</div>
			<h3 className="text-lg font-semibold text-vscode-text-bright font-sans">{title}</h3>
			<p className="text-vscode-text font-mono text-sm">{description}</p>
		</div>
	)
}
